import type { ReactNode } from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, ButtonBase, Typography } from '@mui/material';
import {
  ExpandingPane,
  NotchedOutlinedField,
  notchedOutlineColor,
} from '@core/components';

type OptionalPresenceFieldProps = {
  /** Noun used in “With …” (e.g. Cord, Patching). */
  presenceLabel: string;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  presenceWidth?: string;
  children: ReactNode;
};

/**
 * Outlined presence control. The text stays “With …”; the icon shows on or off.
 * On shrinks the toggle to `presenceWidth` and expands children beside it.
 */
export function OptionalPresenceField({
  presenceLabel,
  enabled,
  onEnabledChange,
  presenceWidth = '25%',
  children,
}: OptionalPresenceFieldProps) {
  const expandedWidth = `calc(100% - ${presenceWidth})`;

  return (
    <NotchedOutlinedField
      sx={{
        containerType: 'inline-size',
        ...(enabled && {
          '&:hover .optional-presence-divider': {
            borderColor: 'text.primary',
          },
        }),
      }}
    >
      <Box
        aria-hidden
        className="optional-presence-divider"
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: presenceWidth,
          borderLeft: '1px solid',
          borderColor: notchedOutlineColor,
          opacity: enabled ? 1 : 0,
          pointerEvents: 'none',
          transition: 'opacity 0.3s ease-in-out, border-color 0.15s ease',
        }}
      />

      <ButtonBase
        onClick={() => onEnabledChange(!enabled)}
        sx={{
          flex: enabled ? '0 0 auto' : 1,
          width: enabled ? presenceWidth : undefined,
          minWidth: 0,
          alignSelf: 'stretch',
          px: 1.5,
          justifyContent: 'flex-start',
          borderRadius: enabled
            ? (theme) =>
                `${theme.shape.borderRadius}px 0 0 ${theme.shape.borderRadius}px`
            : 1,
          transition: 'width 0.3s ease-in-out',
          ...(enabled && {
            '&:hover': { bgcolor: 'action.hover' },
          }),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            whiteSpace: 'nowrap',
          }}
        >
          {enabled ? (
            <CheckCircleIcon color="success" fontSize="small" />
          ) : (
            <CancelIcon color="error" fontSize="small" />
          )}
          <Typography variant="body2">With {presenceLabel}</Typography>
        </Box>
      </ButtonBase>

      <ExpandingPane
        expanded={enabled}
        width={expandedWidth}
        sx={{
          borderRadius: (theme) =>
            `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`,
        }}
      >
        {children}
      </ExpandingPane>
    </NotchedOutlinedField>
  );
}
